import { useEffect, useState } from "react";
import { Menu, X, Search, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, LOGO_URL } from "@/data/news";
import { useLang } from "@/context/LangContext";
import GoogleTranslate from "@/components/site/GoogleTranslate";

const dateFmtHi = () => {
  const d = new Date();
  const days = ["रविवार","सोमवार","मंगलवार","बुधवार","गुरुवार","शुक्रवार","शनिवार"];
  const months = ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितंबर","अक्टूबर","नवंबर","दिसंबर"];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};
const dateFmtEn = () => {
  const d = new Date();
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};
const dateFmt = (lang) => (lang === "en" ? dateFmtEn() : dateFmtHi());

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, lang, cat } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Utility strip */}
      <div
        data-testid="header-utility-strip"
        className="bg-[#0A0A0A] text-[#F4F4F0] font-sans-dev text-[10.5px] md:text-[11px] tracking-[0.16em] md:tracking-[0.18em] uppercase"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2 flex items-center justify-between">
          <span className="truncate">{dateFmt(lang)}<span className="hidden md:inline"> · {lang === "en" ? "Narmadapuram" : "नर्मदापुरम"}</span></span>
          <div className="flex items-center gap-3 md:gap-4 shrink-0 pl-3">
            <a data-testid="epaper-link" className="link-underline" href="/epaper">{t("epaper")}</a>
            <span className="opacity-30">/</span>
            <a data-testid="about-link" className="link-underline hidden sm:inline" href="#about">{t("about")}</a>
            <span className="opacity-30 hidden sm:inline">/</span>
            <a data-testid="contact-top-link" className="link-underline" href="#contact">{t("contact")}</a>
            <span className="opacity-30">/</span>
            <span className="inline-flex"><GoogleTranslate /></span>
          </div>
        </div>
      </div>

      {/* Masthead */}
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 transition-colors ${scrolled ? "bg-[#F4F4F0]/85 backdrop-blur-md" : "bg-[#F4F4F0]"} border-b border-[#0F0F0F]`}
      >
        {/* Desktop: 3-col grid so logo sits perfectly centered and there's no dead space */}
        <div className="hidden md:grid max-w-[1400px] mx-auto px-6 lg:px-8 py-4 grid-cols-3 items-center gap-6">
          <div className="flex items-center gap-5 text-[12px] tracking-[0.22em] uppercase font-sans-dev text-[#4A4A4A]">
            <a href="/search" data-testid="search-btn" className="flex items-center gap-2 hover:text-[#D92525]" aria-label={t("search")}>
              <Search size={16} /> {t("search")}
            </a>
            <span className="opacity-30">/</span>
            <a href="/category/exclusive" className="link-underline">{t("exclusive_link")}</a>
          </div>

          <a href="#" data-testid="brand-logo" className="flex flex-col items-center justify-center">
            <img src={LOGO_URL} alt="Denva Post" className="h-11 w-auto" />
            <span className="font-serif-dev italic text-[11px] text-[#4A4A4A] mt-1.5 tracking-[0.16em] uppercase">
              {t("tagline")}
            </span>
          </a>

          <div className="flex items-center gap-3 justify-end">
            <a
              data-testid="subscribe-cta"
              href="#subscribe"
              className="pill-btn text-[12px]"
            >
              {t("subscribe")} <ChevronRight size={14} />
            </a>
          </div>
        </div>

        {/* Mobile: compact 3-col — hamburger | centered logo | search */}
        <div className="md:hidden grid grid-cols-3 items-center px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="-ml-2 p-2"
              aria-label="मेनू खोलें"
            >
              <Menu size={22} />
            </button>
          </div>
          <a href="#" data-testid="brand-logo-mobile" className="flex items-center justify-center min-w-0">
            <img src={LOGO_URL} alt="Denva Post" className="h-14 sm:h-16 w-auto max-w-full object-contain" />
          </a>
          <div className="flex items-center justify-end gap-1">
            <a href="/search" data-testid="search-btn-mobile" className="p-2 -mr-1" aria-label={t("search")}>
              <Search size={20} />
            </a>
          </div>
        </div>

        {/* Category nav — desktop only */}
        <nav className="border-t border-[#0F0F0F]/15 hidden md:block">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 overflow-x-auto hide-scrollbar">
            <ul className="flex items-stretch justify-center gap-5 lg:gap-6 xl:gap-7 py-3 font-sans-dev text-[12.5px] lg:text-[13px] tracking-wide whitespace-nowrap">
              {CATEGORIES.map((c, i) => (
                <li key={c.key} className={c.dropdown ? "relative group" : ""}>
                  {c.dropdown ? (
                    <>
                      <button
                        type="button"
                        data-testid={`nav-link-${c.key}`}
                        className={`headline-link font-semibold inline-flex items-center gap-1 ${i === 0 ? "text-[#D92525]" : ""}`}
                      >
                        {cat(c.key, c.label)}
                        <ChevronRight size={12} className="rotate-90" />
                      </button>
                      <div
                        data-testid={`nav-dropdown-${c.key}`}
                        className="absolute left-0 top-full mt-1 min-w-[180px] bg-white border border-[#0F0F0F] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-all z-40"
                      >
                        {c.dropdown.map((d) => (
                          <a
                            key={d.url}
                            href={d.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid={`nav-dropdown-${c.key}-${d.label.replace(/\s+/g, "-")}`}
                            className="block px-4 py-2.5 text-sm hover:bg-[#F4F4F0] hover:text-[#D92525] transition-colors border-b border-[#0F0F0F]/10 last:border-0"
                          >
                            {d.label} ↗
                          </a>
                        ))}
                      </div>
                    </>
                  ) : (
                    <a
                      {...(c.external_url
                        ? { href: c.external_url, target: "_blank", rel: "noreferrer noopener" }
                        : { href: c.key === "home" ? "/" : `/category/${c.key}` })}
                      data-testid={`nav-link-${c.key}`}
                      className={`headline-link ${i === 0 ? "text-[#D92525] font-bold" : "font-semibold"}`}
                    >
                      {cat(c.key, c.label)}
                    </a>
                  )}
                </li>
              ))}
              <li>
                <a href="/#videos" data-testid="nav-link-videos" className="headline-link font-semibold text-[#0F0F0F]">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D92525] mr-1.5 -mt-0.5 align-middle" />
                  {t("videos")}
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-50 bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="mobile-drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 left-0 w-[86%] max-w-[360px] bg-[#0A0A0A] text-[#F4F4F0] z-50 flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <img src={LOGO_URL} alt="Denva Post" className="h-8 w-auto" />
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="p-2"
                  aria-label="बंद करें"
                >
                  <X size={20} />
                </button>
              </div>
              <ul className="px-6 py-6 flex flex-col gap-4 font-serif-dev text-[22px] overflow-y-auto">
                {CATEGORIES.map((c) => (
                  <li key={c.key}>
                    {c.dropdown ? (
                      <div data-testid={`mobile-nav-${c.key}`}>
                        <div className="headline-link opacity-70 mb-2">{cat(c.key, c.label)}</div>
                        <ul className="pl-4 space-y-2 border-l border-white/20">
                          {c.dropdown.map((d) => (
                            <li key={d.url}>
                              <a
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${c.key}-${d.label.replace(/\s+/g, "-")}`}
                                href={d.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="headline-link text-[0.85em]"
                              >
                                {d.label} ↗
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <a
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${c.key}`}
                        {...(c.external_url
                          ? { href: c.external_url, target: "_blank", rel: "noreferrer noopener" }
                          : { href: c.key === "home" ? "/" : `/category/${c.key}` })}
                        className="headline-link"
                      >
                        {cat(c.key, c.label)}
                      </a>
                    )}
                  </li>
                ))}
                <li className="border-t border-white/10 pt-4 mt-2">
                  <a onClick={() => setOpen(false)} data-testid="mobile-nav-videos" href="/#videos" className="headline-link flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#D92525] inline-block" /> {t("videos")}
                  </a>
                </li>
                <li>
                  <a onClick={() => setOpen(false)} data-testid="mobile-nav-about" href="#about" className="headline-link">{t("about")}</a>
                </li>
                <li>
                  <a onClick={() => setOpen(false)} data-testid="mobile-nav-contact" href="#contact" className="headline-link">{t("contact")}</a>
                </li>
              </ul>
              <div className="mt-auto px-6 py-5 border-t border-white/10">
                <a onClick={() => setOpen(false)} href="#subscribe" data-testid="mobile-subscribe-cta" className="pill-btn text-[12px] w-full justify-center">
                  {t("subscribe")} <ChevronRight size={14} />
                </a>
                <div className="mt-4 text-[10px] uppercase tracking-widest opacity-60">
                  {dateFmt(lang)}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
